import React, { useState } from 'react';
import Link from 'next/link';
import { FiBarChart } from 'react-icons/fi';
import styled from 'styled-components';
import { Play, DollarSign } from 'react-feather';
import useTranslation from 'next-translate/useTranslation';
import { MdOutlinePlayArrow } from 'react-icons/md';
import { BiDollar, BiShareAlt } from 'react-icons/bi';
import { RiStackLine } from 'react-icons/ri';
import {
  Track,
  TrackArtist,
  TrackRelease,
  style,
  useIsCurrentUser,
} from '../../api';
import {
  TextBold14,
  TextBold22,
  TextBolder14,
  TextRegular18,
} from '../../VuexyDesign/atoms/Text';
import {
  Button,
  Col,
  ProOutlineButton,
  Row,
} from '../../VuexyDesign/atoms/_index';
import { DistributionIconGroup } from '../../VuexyDesign/molecules/_index';
import StatusForTrackDetailPage from './StatusForTrackDetailPage';
import GetTrackInfoOwnerData from './GetTrackInfoOwnerData';

const styleForNonLiveTracks = {
  padding: '4px 15px',
  height: '35px',
  minHeight: '10px',
  borderRadius: '5px',
  fontSize: '13px',
};
const ReleasedTrackInfoDesktop = ({
  track,
  totalStreams,
  totalEarned,
  release,
  contributors,
  otherArtists,
  onShareButtonClick,
}: ReleasedTrackInfoProps & DataProps) => {
  const { t } = useTranslation('tracks');
  const isCurrentUser = useIsCurrentUser(track.user.id);
  const [hovering, setHovering] = useState(false);

  const getExploreTracks = () => (
    <>
      <Link href="/explore">
        <Button
          outline
          color="secondary"
          className="d-flex align-items-center text-dark"
          style={styleForNonLiveTracks}
        >
          <>
            <RiStackLine style={{ marginRight: '5px' }} />
            {t('tracks:exploreOtherTracks') as string}
          </>
        </Button>
      </Link>
    </>
  );

  const getNonLiveTracksData = () => (
    <>
      {isCurrentUser ? (
        <>
          <Row>
            <Col lg={7}>
              <TextBold22>{track.title}</TextBold22>
            </Col>
            <Col
              lg={5}
              className="d-flex align-items-center "
              style={{ opacity: 0.45 }}
            >
              <>
                <TextBolder14 className="mr-1">
                  {t('tracks:availableOn') as string}
                </TextBolder14>
                <DistributionIconGroup
                  iconSize="18"
                  className="ml-1 align-items-center"
                />
              </>
            </Col>
          </Row>
          <Row>
            <Col lg={7}>
              <TextRegular18 className="text-muted">
                {track.user.display_name}
              </TextRegular18>
            </Col>
          </Row>
          <Row className="mt-1 d-flex align-items-center">
            <Col
              lg={4}
              className="text-muted d-flex"
              style={{ paddingLeft: '0.5rem' }}
            >
              <StatusForTrackDetailPage
                isCurrentUser={isCurrentUser}
                track={track}
              />
            </Col>
            <Col lg={8} className="d-flex justify-content-end">
              <Button
                disabled
                outline
                color="secondary"
                className="d-flex align-items-center text-dark py-0 mr-1"
                style={styleForNonLiveTracks}
              >
                <>
                  <FiBarChart size={16} style={{ marginRight: '10px' }} />
                  {t('tracks:stats') as string}
                </>
              </Button>
              <Button
                disabled
                outline
                color="secondary"
                className="d-flex align-items-center text-dark py-0 mr-1"
                style={styleForNonLiveTracks}
              >
                <>
                  <DollarSign size={14} style={{ marginRight: '10px' }} />
                  {t('organisms:earnings') as string}
                </>
              </Button>
              <Button
                disabled
                outline
                color="secondary"
                className="d-flex align-items-center text-dark py-0 mr-1"
                style={styleForNonLiveTracks}
                onClick={onShareButtonClick}
              >
                <>
                  <BiShareAlt size={16} style={{ marginRight: '10px' }} />
                  {t('tracks:share') as string}
                </>
              </Button>
              <StyledButtonProStreamNowForDisabled disabled>
                <>
                  <Play
                    style={{ marginRight: '10px' }}
                    size={14}
                    stroke="#284ed8"
                  />
                  {t('organisms:streamNow') as string}
                </>
              </StyledButtonProStreamNowForDisabled>
            </Col>
          </Row>

          <Row className="mt-5">
            <Col lg={7} className="mt-3">
              <GetTrackInfoOwnerData
                track={track}
                release={release}
                contributors={contributors}
                otherArtists={otherArtists}
              />
            </Col>
            <Col lg={2} />
            <Col lg={3} className="mt-3">
              {getExploreTracks()}
            </Col>
          </Row>
        </>
      ) : (
        <>
          <Row>
            <Col lg={6}>
              <TextBold22>{track.title}</TextBold22>
            </Col>
            <Col
              lg={6}
              className="d-flex align-items-center"
              style={{ opacity: 0.45 }}
            >
              <TextBolder14 className="mr-1">
                {t('tracks:comingSoonOn') as string}
              </TextBolder14>
              <DistributionIconGroup
                iconSize="18"
                className="ml-1 align-items-center"
              />
            </Col>
          </Row>
          <Row>
            <Col lg={7}>
              <TextRegular18 className="text-muted">
                {track.user.display_name}
              </TextRegular18>
            </Col>
          </Row>
          <Row className="mt-1 d-flex align-items-center">
            <Col
              lg={7}
              className="text-muted d-flex"
              style={{ paddingLeft: '0.5rem' }}
            >
              <StatusForTrackDetailPage
                isCurrentUser={isCurrentUser}
                track={track}
              />
            </Col>
            <Col lg={5} className="d-flex">
              <StyledButtonProStreamNowForDisabled className="mr-1" disabled>
                <>
                  <Play
                    style={{ marginRight: '10px' }}
                    size={14}
                    stroke="#284ed8"
                  />
                  {t('organisms:streamNow') as string}
                </>
              </StyledButtonProStreamNowForDisabled>
              <Button
                disabled
                outline
                color="secondary"
                className="d-flex align-items-center text-dark py-0"
                style={styleForNonLiveTracks}
                onClick={onShareButtonClick}
              >
                <>
                  <BiShareAlt size={16} style={{ marginRight: '10px' }} />
                  {t('tracks:share') as string}
                </>
              </Button>
            </Col>
          </Row>

          <Row className="mt-5">
            <Col lg={7} className="mt-3">
              <GetTrackInfoOwnerData
                track={track}
                release={release}
                contributors={contributors}
                otherArtists={otherArtists}
              />
            </Col>
            <Col lg={2} />
            <Col lg={3} className="mt-3">
              {getExploreTracks()}
            </Col>
          </Row>
        </>
      )}
    </>
  );

  const getLiveTracksData = () => (
    <>
      {isCurrentUser ? (
        <>
          <Row>
            <Col lg={7}>
              <TextBold22>{track.title}</TextBold22>
            </Col>
            <Col lg={5} className="d-flex align-items-center ">
              <>
                <TextBolder14 className="mr-1">
                  {t('tracks:availableOn') as string}
                </TextBolder14>
                <DistributionIconGroup
                  iconSize="18"
                  className="ml-1 align-items-center"
                />
              </>
            </Col>
          </Row>
          <Row>
            <Col lg={7}>
              <TextRegular18 className="text-muted">
                {track.user.display_name}
              </TextRegular18>
            </Col>
          </Row>
          <Row className="mt-1">
            <Col
              lg={7}
              className="text-muted d-flex"
              style={{ paddingLeft: '0.5rem' }}
            >
              <div className="d-flex align-items-center">
                <MdOutlinePlayArrow size={24} />
                <TextBold14>
                  {totalStreams} {t('tracks:overallStreams') as string}
                </TextBold14>
              </div>
              {totalEarned > 20 ? (
                <div className="d-flex align-items-center ml-3">
                  <BiDollar size={20} />
                  <TextBold14>
                    {totalEarned}$ {t('tracks:earned') as string}
                  </TextBold14>
                  <Link href="/statistics?tab=earnings">
                    <TextBold14 className="text-info ml-1 cursor-pointer">
                      {t('tracks:transferToWallet') as string}
                    </TextBold14>
                  </Link>
                </div>
              ) : (
                <></>
              )}
            </Col>
            <Col lg={5} className="d-flex">
              <Link href={`/track-releases/${track.id}`}>
                <StyledButtonProStreamNow
                  className="mr-1"
                  onMouseEnter={() => setHovering(true)}
                  onMouseLeave={() => setHovering(false)}
                >
                  <>
                    {t('organisms:streamNow') as string}
                    <Play
                      style={{ marginLeft: '10px' }}
                      size={14}
                      stroke={hovering ? '#fff' : '#284ed8'}
                    />
                  </>
                </StyledButtonProStreamNow>
              </Link>

              <Link href={`/statistics?tab=releases&track_id=${track.id}`}>
                <Button
                  outline
                  color="secondary"
                  className="d-flex align-items-center text-dark py-0 mr-1"
                  style={styleForNonLiveTracks}
                >
                  <>
                    <FiBarChart size={16} style={{ marginRight: '10px' }} />
                    {t('tracks:stats') as string}
                  </>
                </Button>
              </Link>
              <Button
                outline
                color="secondary"
                className="d-flex align-items-center text-dark py-0"
                style={styleForNonLiveTracks}
                onClick={onShareButtonClick}
              >
                <>
                  <BiShareAlt size={16} style={{ marginRight: '10px' }} />
                  {t('tracks:share') as string}
                </>
              </Button>
            </Col>
          </Row>
          <Row className="mt-5">
            <Col lg={7} className="mt-3">
              <GetTrackInfoOwnerData
                track={track}
                release={release}
                contributors={contributors}
                otherArtists={otherArtists}
              />
            </Col>
            <Col lg={2} />
            <Col lg={3} className="mt-3">
              {getExploreTracks()}
            </Col>
          </Row>
        </>
      ) : (
        <>
          <Row>
            <Col lg={7}>
              <TextBold22>{track.title}</TextBold22>
            </Col>
          </Row>
          <Row>
            <Col lg={7}>
              <TextRegular18 className="text-muted">
                {track.user.display_name}
              </TextRegular18>
            </Col>
          </Row>
          <Row className="mt-1">
            <Col lg={7} className="d-flex">
              <Link href={`/track-releases/${track.id}`}>
                <StyledButtonProStreamNow
                  className="mr-1"
                  onMouseEnter={() => setHovering(true)}
                  onMouseLeave={() => setHovering(false)}
                >
                  <>
                    {t('organisms:streamNow') as string}
                    <Play
                      style={{ marginLeft: '10px' }}
                      size={14}
                      stroke={hovering ? '#fff' : '#284ed8'}
                    />
                  </>
                </StyledButtonProStreamNow>
              </Link>

              <Button
                outline
                color="secondary"
                className="d-flex align-items-center text-dark py-0"
                style={styleForNonLiveTracks}
                onClick={onShareButtonClick}
              >
                <>
                  <BiShareAlt size={16} style={{ marginRight: '10px' }} />
                  <TextBold14>{t('tracks:share') as string}</TextBold14>
                </>
              </Button>
            </Col>
            <Col lg={5} className="d-flex align-items-center ">
              <>
                <TextBolder14 className="mr-1">
                  {t('tracks:availableOn') as string}
                </TextBolder14>
                <DistributionIconGroup
                  iconSize="18"
                  className="ml-1 align-items-center"
                />
              </>
            </Col>
          </Row>
          <Row className="mt-5">
            <Col lg={7} className="mt-3">
              <GetTrackInfoOwnerData
                track={track}
                release={release}
                contributors={contributors}
                otherArtists={otherArtists}
              />
            </Col>
            <Col lg={2} />
            <Col lg={3} className="mt-3">
              {getExploreTracks()}
            </Col>
          </Row>
        </>
      )}
    </>
  );

  return (
    <>
      {track.release_status === 'live'
        ? getLiveTracksData()
        : getNonLiveTracksData()}
    </>
  );
};

export default ReleasedTrackInfoDesktop;

interface ReleasedTrackInfoProps {
  track: Track;
}

interface DataProps {
  totalStreams: number;
  totalEarned: number;
  onShareButtonClick: () => void;
  release: TrackRelease;
  contributors: TrackArtist[];
  otherArtists: TrackArtist[];
}
const StyledButtonProStreamNowForDisabled = styled.button`
  background: ${style.distributionGradient};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  white-space: nowrap;
  box-shadow: none;

  padding: 4px 15px;
  font-size: 13px;
  height: 35px;
  min-height: 10px;

  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 0;
  text-decoration: none;
  border-radius: 5px;

  :before {
    content: '';
    position: absolute;
    z-index: -1;
    inset: 0;
    border: 2px solid transparent;
    border-radius: 5px;
    background: inherit;
    background-origin: border-box;
    background-clip: border-box;
    -webkit-mask: linear-gradient(#fff 0 0) padding-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    -webkit-mask-repeat: no-repeat;
  }
`;
const StyledButtonProStreamNow = styled(ProOutlineButton)`
  padding: 4px 15px !important;
  font-size: 13px !important;
  height: 35px !important;
  min-height: 10px !important;
`;
